import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Phone, Mail } from 'lucide-react';

import { Button } from '../../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { OtpInput } from '../../components/ui/OtpInput';
import { buildScheme } from '../DeepLinkUtils';
import { MOBILE_APP_PAUSED } from '../../constants/appStore';
import { supabase } from '../../lib/supabase';

type OtpType = 'recovery' | 'signup' | 'magiclink' | 'invite' | 'sms';

export default function VerifyOtp() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email') || '';
  const phone = searchParams.get('phone') || '';
  const type = (searchParams.get('type') as OtpType) || (email ? 'magiclink' : 'sms');
  const companyId = searchParams.get('companyId') || '';
  const locationId = searchParams.get('locationId') || '';
  
  const isPhoneVerification = type === 'sms' || !!phone;
  
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  // Detect mobile and redirect to app — skipped while MOBILE_APP_PAUSED so users
  // complete OTP verification on the web instead of bouncing off a delisted app.
  React.useEffect(() => {
    if (MOBILE_APP_PAUSED) return;

    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    if (isMobile) {
      const params = new URLSearchParams();
      if (email) params.set('email', email);
      if (phone) params.set('phone', phone);
      if (type) params.set('type', type);
      if (companyId) params.set('companyId', companyId);
      if (locationId) params.set('locationId', locationId);

      const mobileDeepLink = buildScheme(`/verify-otp?${params.toString()}`);
      window.location.replace(mobileDeepLink);
    }
  }, [email, phone, type, companyId, locationId]);

  // Resend cooldown timer
  React.useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const formatPhoneForDisplay = (phoneNumber: string): string => {
    // Format +1XXXXXXXXXX -> (XXX) XXX-XXXX
    const digits = phoneNumber.replace(/\D/g, '').slice(1); // Remove +1
    if (digits.length === 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    return phoneNumber;
  };

  const handleVerify = async (otpCode: string) => {
    if (!email && !phone) {
      setError('Email or phone number is required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let verifyError;

      if (isPhoneVerification && phone) {
        // Phone-based OTP verification
        const { error } = await supabase.auth.verifyOtp({
          phone,
          token: otpCode,
          type: 'sms',
        });
        verifyError = error;
      } else if (email) {
        // Email-based OTP verification
        const { error } = await supabase.auth.verifyOtp({
          email,
          token: otpCode,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          type: type as any,
        });
        verifyError = error;
      } else {
        setError('Email or phone number is required');
        setLoading(false);
        return;
      }

      if (verifyError) {
        setError(verifyError.message);
        return;
      }

      // Success - verifyOtp sets the session automatically
      // Navigate based on type and context
      if (type === 'recovery') {
        navigate('/reset-password');
      } else if (type === 'invite' || (email && companyId)) {
        // Draft invite flow
        const params = new URLSearchParams();
        if (companyId) params.set('companyId', companyId);
        if (locationId) params.set('locationId', locationId);
        navigate(`/draft-invite-auth?${params.toString()}`);
      } else if (type === 'signup' || isPhoneVerification) {
        // Phone signup - check if we have company/location for signup flow
        if (companyId) {
          const signupParams = new URLSearchParams();
          signupParams.set('companyId', companyId);
          if (locationId) signupParams.set('locationId', locationId);
          if (email) signupParams.set('email', email);
          navigate(`/signup?${signupParams.toString()}`);
        } else {
          // Regular signup - go to onboarding
          navigate('/onboarding');
        }
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0 || (!email && !phone)) return;

    setResendLoading(true);
    setError(null);

    try {
      let resendError;

      if (isPhoneVerification && phone) {
        // Resend phone OTP
        const { error } = await supabase.auth.signInWithOtp({
          phone,
          options: {
            shouldCreateUser: true,
            data: {
              signup_intent: email ? 'draft_student_invite' : 'student_signup',
              ...(email && { email }),
              ...(companyId && { company_id: companyId }),
              ...(locationId && { location_id: locationId }),
            },
          },
        });
        resendError = error;
      } else if (email) {
        // Email-based resend
        if (type === 'recovery') {
          const redirectTo = `${window.location.origin}/reset-password`;
          const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo });
          resendError = error;
        } else {
          const { error } = await supabase.auth.signInWithOtp({
            email,
            options: {
              shouldCreateUser: type === 'signup',
              data: {
                signup_intent: type === 'invite' ? 'draft_student_invite' : 'student_signup',
              },
            },
          });
          resendError = error;
        }
      }

      if (resendError) {
        setError(resendError.message);
      } else {
        setResendCooldown(60);
        setError(null);
      }
    } catch (err) {
      setError('Failed to resend code. Please try again.');
    } finally {
      setResendLoading(false);
    }
  };

  const getTitle = () => {
    switch (type) {
      case 'recovery':
        return 'Reset Password';
      case 'signup':
        return isPhoneVerification ? 'Confirm Phone' : 'Confirm Email';
      case 'magiclink':
        return 'Sign In';
      case 'invite':
        return 'Enter Invite Code';
      case 'sms':
        return 'Verify Phone';
      default:
        return 'Verify Code';
    }
  };

  const getSubtitle = () => {
    if (isPhoneVerification && phone) {
      return 'Enter the 6-digit code sent to your phone.';
    }
    switch (type) {
      case 'recovery':
        return 'Enter the 6-digit code from your email to reset your password.';
      case 'signup':
        return 'Enter the 6-digit code from your email to confirm your account.';
      case 'magiclink':
        return 'Enter the 6-digit code from your email to sign in.';
      case 'invite':
        return 'Enter the 6-digit code from your email to accept your invitation.';
      default:
        return 'Enter the 6-digit code from your email.';
    }
  };

  const getTargetDisplay = () => {
    if (isPhoneVerification && phone) {
      return formatPhoneForDisplay(phone);
    }
    return email;
  };

  if (!email && !phone) {
    return (
      <div className="min-h-screen bg-[#111010] flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center justify-center py-8">
              <h2 className="text-xl font-semibold text-white mb-2">Invalid Request</h2>
              <p className="text-gray-400 text-sm text-center mb-4">
                No email or phone number found. Please use the verification link or code entry page.
              </p>
              <Button onClick={() => navigate('/signin')} variant="outline">
                Go to Sign In
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#111010] flex items-center justify-center p-4">
      <Card className="w-full max-w-sm sm:max-w-md border-[#504A4A] bg-[#221F1F]">
        <CardHeader>
          <CardTitle className="text-white text-center text-lg sm:text-xl flex items-center justify-center gap-2">
            {isPhoneVerification ? <Phone className="w-5 h-5" /> : <Mail className="w-5 h-5" />}
            {getTitle()}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-gray-300 text-center mb-2 text-sm sm:text-base">
            {getSubtitle()}
          </p>
          <p className="text-gray-400 text-center mb-6 sm:mb-8 font-mono text-xs sm:text-sm break-all">
            {getTargetDisplay()}
          </p>

          <div className="mb-6">
            <OtpInput
              length={6}
              onComplete={handleVerify}
              onChange={setCode}
              error={!!error}
              disabled={loading}
            />
          </div>

          {error && (
            <p className="text-red-500 text-sm text-center mb-4">{error}</p>
          )}

          <Button
            onClick={() => handleVerify(code)}
            disabled={loading || code.length !== 6}
            className="w-full bg-[#e11d48] hover:bg-[#be1e3e] mb-4"
          >
            {loading ? 'Verifying...' : 'Verify Code'}
          </Button>

          <div className="text-center">
            <button
              onClick={handleResend}
              disabled={resendLoading || resendCooldown > 0}
              className="text-sm text-[#00D9FF] hover:text-[#00B8D9] disabled:text-gray-500 disabled:cursor-not-allowed"
            >
              {resendLoading
                ? 'Sending...'
                : resendCooldown > 0
                ? `Resend in ${resendCooldown}s`
                : 'Resend Code'}
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}







